import React, { useState } from 'react';
import { Bell, Check, X, Trash2, CheckCheck, ArrowLeft, Filter, Settings, TrendingUp, Gift, Target } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import BackButton from '../components/BackButton';

const NotificationPage = () => {
  const [filterType, setFilterType] = useState('all'); // all, unread, read
  const [categoryFilter, setCategoryFilter] = useState('all'); // all, system, prediction, market, activity
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    removeNotification,
    clearAllNotifications 
  } = useNotifications();

  // 过滤通知
  const filteredNotifications = notifications.filter(notification => {
    // 按读取状态过滤
    let passesReadFilter = true;
    if (filterType === 'unread') passesReadFilter = !notification.isRead;
    if (filterType === 'read') passesReadFilter = notification.isRead;
    
    // 按分类过滤
    let passesCategoryFilter = true;
    if (categoryFilter !== 'all') passesCategoryFilter = notification.category === categoryFilter;
    
    return passesReadFilter && passesCategoryFilter;
  });

  // 获取各分类的数量
  const getCategoryCount = (category) => {
    if (category === 'all') return notifications.length;
    return notifications.filter(n => n.category === category).length;
  };

  // 获取分类图标
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'system': return Settings;
      case 'prediction': return Target;
      case 'market': return TrendingUp;
      case 'activity': return Gift;
      default: return Bell;
    }
  };

  // 获取分类名称
  const getCategoryName = (category) => {
    switch (category) {
      case 'system': return 'System';
      case 'prediction': return 'Predictions';
      case 'market': return 'Market';
      case 'activity': return 'Activities';
      default: return 'All';
    }
  };

  // 格式化时间
  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // 获取通知类型图标
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <Check className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <Bell className="w-5 h-5 text-orange-400" />;
      case 'error':
        return <X className="w-5 h-5 text-red-400" />;
      default:
        return <Bell className="w-5 h-5 text-blue-400" />;
    }
  };

  // 过滤器按钮样式
  const getFilterButtonClass = (type) => {
    const isActive = filterType === type;
    return `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive 
        ? 'bg-blue-500 text-white' 
        : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
    }`;
  };

  // 分类过滤器按钮样式
  const getCategoryButtonClass = (category) => {
    const isActive = categoryFilter === category;
    return `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive 
        ? 'bg-purple-500 text-white' 
        : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
    }`;
  };

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div>
                <h1 className="text-2xl font-bold text-white">Notifications</h1>
                <p className="text-gray-400">
                  {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
                </p>
              </div>
            </div>
          </div>
          
          {/* 操作按钮 */}
          <div className="flex items-center space-x-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                <span>Mark all read</span>
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAllNotifications}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Clear all</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        {/* 过滤器 */}
        <div className="mb-6 space-y-4">
          {/* 状态过滤器 */}
          <div className="flex items-center space-x-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-400 font-medium">Status:</span>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setFilterType('all')}
                className={getFilterButtonClass('all')}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setFilterType('unread')}
                className={getFilterButtonClass('unread')}
              >
                Unread ({unreadCount})
              </button>
              <button
                onClick={() => setFilterType('read')}
                className={getFilterButtonClass('read')}
              >
                Read ({notifications.length - unreadCount})
              </button>
            </div>
          </div>

          {/* 分类过滤器 */}
          <div className="flex items-center space-x-4">
            <div className="w-5 h-5"></div> {/* 占位符保持对齐 */}
            <span className="text-sm text-gray-400 font-medium">Category:</span>
            <div className="flex items-center space-x-3 flex-wrap">
              <button
                onClick={() => setCategoryFilter('all')}
                className={getCategoryButtonClass('all')}
              >
                <Bell className="w-4 h-4" />
                <span>All ({getCategoryCount('all')})</span>
              </button>
              <button
                onClick={() => setCategoryFilter('system')}
                className={getCategoryButtonClass('system')}
              >
                <Settings className="w-4 h-4" />
                <span>System ({getCategoryCount('system')})</span>
              </button>
              <button
                onClick={() => setCategoryFilter('prediction')}
                className={getCategoryButtonClass('prediction')}
              >
                <Target className="w-4 h-4" />
                <span>Predictions ({getCategoryCount('prediction')})</span>
              </button>
              <button
                onClick={() => setCategoryFilter('market')}
                className={getCategoryButtonClass('market')}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Market ({getCategoryCount('market')})</span>
              </button>
              <button
                onClick={() => setCategoryFilter('activity')}
                className={getCategoryButtonClass('activity')}
              >
                <Gift className="w-4 h-4" />
                <span>Activities ({getCategoryCount('activity')})</span>
              </button>
            </div>
          </div>
        </div>

        {/* 通知列表 */}
        <div className="bg-white/5 rounded-xl border border-white/10">
          {filteredNotifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-400 opacity-50" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {filterType === 'unread' ? 'No unread notifications' : 
                 filterType === 'read' ? 'No read notifications' : 'No notifications'}
              </h3>
              <p className="text-gray-400">
                {filterType === 'all' 
                  ? "You're all caught up! New notifications will appear here."
                  : `No ${filterType} notifications found.`
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {filteredNotifications.map((notification, index) => (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-white/5 transition-colors ${
                    !notification.isRead ? 'bg-blue-500/5 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    {/* 图标 */}
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    {/* 内容 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className={`text-lg font-semibold mb-2 ${
                            !notification.isRead ? 'text-white' : 'text-gray-300'
                          }`}>
                            {notification.title}
                          </h4>
                          <p className="text-gray-400 mb-3 leading-relaxed">
                            {notification.message}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{formatTime(notification.timestamp)}</span>
                            {/* 分类标签 */}
                            <div className="flex items-center space-x-1">
                              {React.createElement(getCategoryIcon(notification.category), { 
                                className: "w-3 h-3" 
                              })}
                              <span className="text-xs font-medium text-gray-400 uppercase">
                                {getCategoryName(notification.category)}
                              </span>
                            </div>
                            {!notification.isRead && (
                              <span className="flex items-center space-x-1 text-blue-400">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span>Unread</span>
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* 操作按钮 */}
                        <div className="flex items-center space-x-2 ml-4">
                          {!notification.isRead && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-colors"
                              title="Mark as read"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => removeNotification(notification.id)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Remove notification"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPage;
